import {useState} from 'react';
import {pickFile, AUDIO_ACCEPT} from '../lib/browserFiles';

interface AudioPathModalProps {
  isOpen: boolean;
  onConfirm: (audioName: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * Asks which media file a re-uploaded CSV belongs to.
 *
 * On the desktop app this captured an absolute path, which Convert later re-read to probe sample
 * rate and duration. The browser has no such access, so we only capture the file *name* as a label.
 * Probing has to happen server-side at upload time — see docs/BACKEND_REQUESTS.md.
 */
export const AudioPathModal = ({
  isOpen,
  onConfirm,
  onCancel,
  isLoading = false,
}: AudioPathModalProps) => {
  const [audioName, setAudioName] = useState('');

  if (!isOpen) return null;

  const handleSelectFile = async () => {
    const file = await pickFile({accept: AUDIO_ACCEPT});
    if (file) {
      setAudioName(file.name);
    }
  };

  const handleConfirm = () => {
    if (audioName.trim()) {
      onConfirm(audioName.trim());
      setAudioName('');
    }
  };

  const handleCancel = () => {
    setAudioName('');
    onCancel();
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{backgroundColor: 'rgba(0, 0, 0, 0.3)'}}
      onClick={handleCancel}
    >
      <div
        className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-white mb-2">Which media file is this for?</h2>
        <p className="text-gray-300 text-sm mb-4">
          Name the original audio/video file this transcription came from, so you can recognise it
          later when converting.
        </p>

        <label className="block text-gray-300 text-xs mb-2 font-medium" htmlFor="audioName">
          Audio file name
        </label>
        <input
          id="audioName"
          type="text"
          value={audioName}
          onChange={e => setAudioName(e.target.value)}
          disabled={isLoading}
          placeholder="e.g. episode-12.wav"
          className="w-full px-3 py-2 mb-3 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
        />

        <button
          onClick={handleSelectFile}
          disabled={isLoading}
          className="w-full px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-500 transition-colors mb-3 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          📁 Pick a file to fill in the name
        </button>

        <div className="flex gap-3">
          <button
            onClick={handleConfirm}
            disabled={!audioName.trim() || isLoading}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-500 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? '⏳ Uploading...' : '✓ Continue'}
          </button>
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
